import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface Attachment {
  id: string;
  name: string;
  size?: number;
  type?: string;
  data?: Blob;
  s3_key?: string;
  file_name?: string;
  content_type?: string;
  size_in_bytes?: number;
  isLoading?: boolean;
  uploadError?: string;
}

interface EmailTemplate {
  _id: string;
  name: string;
  subject: string;
  content: string;
  created_by: string;
  last_updated_by: string;
  created_at: string;
  updated_at: string;
  attachments?: {
    s3_key: string;
    file_name: string;
    content_type: string;
    size_in_bytes: number;
  }[];
}

interface EmailEditorState {
  content: string;
  subject: string;
  recipients: string[];
  ccList: string[];
  bccList: string[];
  attachments: Attachment[];
  selectedTemplateId: string | null;
  selectedTemplate: EmailTemplate | null;
  selectedTab: string;
  unsubscribeEnabled: boolean;
}

const initialState: EmailEditorState = {
  content: "",
  subject: "",
  recipients: [],
  ccList: [],
  bccList: [],
  attachments: [],
  selectedTemplateId: null,
  selectedTemplate: null,
  selectedTab: "create-email",
  unsubscribeEnabled: false,
};

export const emailEditorSlice = createSlice({
  name: "emailEditor",
  initialState,
  reducers: {
    setContent: (state, action: PayloadAction<string>) => {
      state.content = action.payload;
    },
    setSubject: (state, action: PayloadAction<string>) => {
      state.subject = action.payload;
    },
    setRecipients: (state, action: PayloadAction<string[]>) => {
      state.recipients = action.payload;
    },
    setCcList: (state, action: PayloadAction<string[]>) => {
      state.ccList = action.payload;
    },
    setBccList: (state, action: PayloadAction<string[]>) => {
      state.bccList = action.payload;
    },
    setAttachments: (
      state,
      action: PayloadAction<
        Attachment[] | ((prev: Attachment[]) => Attachment[])
      >
    ) => {
      if (typeof action.payload === "function") {
        state.attachments = action.payload(state.attachments);
      } else {
        state.attachments = action.payload;
      }
    },
    setSelectedTemplateId: (state, action: PayloadAction<string | null>) => {
      state.selectedTemplateId = action.payload;
    },
    setSelectedTemplate: (
      state,
      action: PayloadAction<EmailTemplate | null>
    ) => {
      state.selectedTemplate = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
    setUnsubscribeEnabled: (state, action: PayloadAction<boolean>) => {
      state.unsubscribeEnabled = action.payload;
    },
    resetEmailEditor: (state) => {
      state.content = "";
      state.subject = "";
      state.recipients = [];
      state.ccList = [];
      state.bccList = [];
      state.attachments = [];
      state.selectedTemplateId = null;
      state.selectedTemplate = null;
      state.selectedTab = "create-email";
      state.unsubscribeEnabled = false;
    },
  },
});

export const {
  setContent,
  setSubject,
  setRecipients,
  setCcList,
  setBccList,
  setAttachments,
  setSelectedTemplateId,
  setSelectedTemplate,
  setSelectedTab,
  setUnsubscribeEnabled,
  resetEmailEditor,
} = emailEditorSlice.actions;
